import './styles.scss';

import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import { Time, TimeRangeChangeEventHandler, Range } from 'lightweight-charts';
import { throttle } from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import AddSeriesMenu from '../../../../components/add-series-menu';
import VolSeriesChip from '../../../../components/series-chip/vol_series_chip';
import { CHART_EVENTS_KEYS, VOL_SURFACES, TENORS } from '../../../../constants/websocket-events';
import { ChartTypeEnum } from '../../../../model/chart_type.enum';
import { CurrencyTypeEnum } from '../../../../model/currency_type.enum';
import { RangeEnum } from '../../../../model/range.enum';
import { MarketDataContext } from '../../../../providers/market-data/market-data-provider';
import { SnapshotContext } from '../../../../providers/snapshot-provider';
import Utils from '../../../../utils/immersive_utils';

import { MarketHistoryHeader } from './components/market-history-header';
import { MarketHistoryRangeControls } from './components/market-history-range-controls';
import { useChartSetup } from './hooks/use-chart-setup';
import { useSyncedCharts } from './hooks/use-synced-charts';
import { makeSeriesLabel } from '../../../../utils/string';
import { getCurrentDate } from '../../../../utils/date';
import { useCrosshairMove } from '../../../../providers/market-data/hooks/use-crosshair-move';
import { PerpetualDataPoint } from '../../../../providers/market-data/market-data.types';
import { WebSocketContext } from '../../../../providers/websocket-context/websocket-provider';

interface ChartProperties {
  chartType: ChartTypeEnum;
  keyParam: string;
  disableCurrencyChange?: boolean;
}

export const MarketHistoryContent = (_props: ChartProperties) => {
  const { token: currency } = useContext(MarketDataContext);

  const { isConnected } = useContext(WebSocketContext);

  const {
    chart,
    chartExtra,
    chartType,
    setChartType,
    hasFlyChart,
    chartContainerRef,
    chartExtraContainerRef,
    unsubscribe,
    isLoadingPerpetualHistorical,
    setIsLoadingPerpetualHistorical,
    isLoadingVolsHistorical,
    setLoadingVolsHistorical,
    range,
    setRange,
    series,
    addSeries,
    removeSeries,
    fetchVolsHistorical,
    fetchPerpetualHistorical,
    handleRangeChange,
    generateCompositeId,
    isFetchingCmvolSeries,
    isFetchingPerpetualSeries,
    isToStopGraphsSyncRef,
  } = useContext(MarketDataContext);

  const { handleClickPointVolSeries } = useContext(SnapshotContext);

  const chartBoxContainerRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<any>();
  const resizeBarRef = useRef<HTMLDivElement>(null);
  const rafId: any = useRef(null);
  const isResizing = useRef(false);
  const earliestRef = useRef<number>(Infinity);
  const lastRequestRef = useRef<number>(Infinity);
  const isLoadingMoreRef = useRef<boolean>(false);

  const isChartLoading = isLoadingPerpetualHistorical || isLoadingVolsHistorical;
  const isWrapperLoading =
    isFetchingCmvolSeries ||
    ((isLoadingPerpetualHistorical || isLoadingVolsHistorical) && isToStopGraphsSyncRef.current);

  const chipStyle = (isLoading: boolean) => ({
    opacity: isLoading ? 0 : 1,
    pointerEvents: isLoading ? 'none' : ('auto' as React.CSSProperties['pointerEvents']),
  });

  const CMVOL_PERCENT: string[] = [];

  VOL_SURFACES.forEach((volSurface) => {
    TENORS.forEach((tenor) => {
      CMVOL_PERCENT.push(`CMVOL_${volSurface}_${tenor}`);
    });
  });

  const [areChartsReady, setAreChartsReady] = useState(false);
  const [perpetualHoverData, setPerpetualHoverData] = useState<PerpetualDataPoint | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      if (chart.current && chartExtra.current) {
        setAreChartsReady(true);
        clearInterval(timer);
      }
    }, 50);
    return () => clearInterval(timer);
  }, []);

  const theme = useTheme();
  const { initMainChart } = useChartSetup();

  const handleApplyCalendar = async (startDate: Date | string, endDate: Date | string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (!Utils.isValidDate(start) || !Utils.isValidDate(end)) {
      return;
    }

    const diffMs = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    let newRange: RangeEnum;
    if (diffDays <= 2) newRange = RangeEnum.D1;
    else if (diffDays <= 5) newRange = RangeEnum.D5;
    else if (diffDays <= 30) newRange = RangeEnum.M1;
    else if (diffDays <= 91) newRange = RangeEnum.M3;
    else if (diffDays <= 182) newRange = RangeEnum.M6;
    else newRange = RangeEnum.Y1;

    end.setSeconds(0);
    end.setMilliseconds(0);
    start.setSeconds(0);
    start.setMilliseconds(0);

    setRange(newRange);
    setIsLoadingPerpetualHistorical(true);
    unsubscribe(generateCompositeId(CHART_EVENTS_KEYS.CANDLE_SUBSCRIPTION));
    await fetchPerpetualHistorical({
      endDate: end,
      range: newRange,
      options: { calendarStart: start },
    });

    const volQueries = Object.values(series)
      .filter((s) => s.type !== 'perp')
      .map((s) => {
        const [, label, tenor] = s.id.split('_');
        return { label, tenor };
      });

    if (volQueries.length > 0) {
      setLoadingVolsHistorical(true);
      unsubscribe(generateCompositeId('CMVOL_SUBSCRIPTION'));
      fetchVolsHistorical({
        endDate: end,
        queries: volQueries,
        newRange,
        options: { calendarStart: start },
      });
    }
  };

  const getCurrentQueries = () => {
    return Object.values(series)
      .filter((s) => s.type !== 'perp')
      .map((s) => {
        const [label, tenor] = s.label.split(' ');
        return { label, tenor };
      });
  };

  useEffect(() => {
    const perp = series[CHART_EVENTS_KEYS.CANDLE_PERPETUAL];
    if (perp?.data?.length) {
      earliestRef.current = perp.data[0].time as number;
      isLoadingMoreRef.current = false;
      lastRequestRef.current = Infinity;
    }
  }, [series]);

  const handleTimeChange = useCallback<TimeRangeChangeEventHandler<Time>>(
    throttle(async (timeRange: Range<Time> | null) => {
      if (
        isToStopGraphsSyncRef.current ||
        isLoadingPerpetualHistorical ||
        isLoadingVolsHistorical ||
        !chart.current
      )
        return;

      if (!timeRange?.from) return;

      const MIN_DATE = Date.UTC(2019, 3, 30, 0, 0, 0);
      const visibleFromMs = Math.floor((timeRange.from as number) * 1000);
      if (visibleFromMs <= MIN_DATE) return;

      const fromSec = Math.floor(timeRange.from as number);
      if (isLoadingMoreRef.current) return;

      if (fromSec <= earliestRef.current && fromSec < lastRequestRef.current) {
        lastRequestRef.current = fromSec;
        isLoadingMoreRef.current = true;

        const backwardDate = new Date(earliestRef.current * 1000);
        if (!(backwardDate instanceof Date) || isNaN(backwardDate.getTime())) return;

        fetchPerpetualHistorical({
          endDate: backwardDate,
          range,
          options: { isBackward: true },
        });

        const queries = getCurrentQueries();
        if (queries.length) {
          fetchVolsHistorical({
            endDate: backwardDate,
            queries,
            options: { isBackward: true },
          });
        }
      }
    }, 200) as TimeRangeChangeEventHandler<Time>,
    [
      isLoadingVolsHistorical,
      isLoadingPerpetualHistorical,
      series,
      range,
      fetchPerpetualHistorical,
      fetchVolsHistorical,
    ],
  );

  useEffect(() => {
    if (!chart.current) return;
    chart.current.timeScale().subscribeVisibleTimeRangeChange(handleTimeChange);

    return () => {
      chart.current?.timeScale().unsubscribeVisibleTimeRangeChange(handleTimeChange);
    };
  }, [handleTimeChange]);

  const { validateNow } = useSyncedCharts({
    mainChartRef: chart,
    extraChartRef: chartExtra,
    pauseRef: isToStopGraphsSyncRef,
    enabled:
      areChartsReady &&
      hasFlyChart &&
      !isLoadingVolsHistorical &&
      !isLoadingPerpetualHistorical &&
      !isFetchingPerpetualSeries &&
      !isFetchingCmvolSeries,
    deps: [
      chart.current,
      chartExtra.current,
      areChartsReady,
      isFetchingPerpetualSeries,
      isFetchingCmvolSeries,
      isLoadingVolsHistorical,
      isLoadingPerpetualHistorical,
      hasFlyChart,
    ],
  });

  const handleClick = (param: any) => {
    if (param.time) {
      handleClickPointVolSeries(param.time);
    }
  };

  useEffect(() => {
    if (!chart.current) {
      const actions = { subscribeClick: handleClick };

      initMainChart({
        containerRef: chartContainerRef,
        chartRef: chart,
        actions,
        options: {
          autoSize: false,
        },
      });
    }
  }, [theme, initMainChart]);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (entry.target === chartContainerRef.current) {
          chart.current?.resize(width, height);
        }
        if (entry.target === chartExtraContainerRef.current) {
          chartExtra.current?.resize(width, height);
        }
        validateNow();
      }
    });
    ro.observe(chartContainerRef.current);
    if (chartExtraContainerRef.current) ro.observe(chartExtraContainerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const container = chartBoxContainerRef.current;
    if (!container) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!(e.target as Element).closest('.resize-bar')) return;
      isResizing.current = true;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isResizing.current) return;
      e.preventDefault();
      if (rafId.current != null) return;
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        const rect = container.getBoundingClientRect();
        const offsetY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
        const mainRatio = offsetY / rect.height;
        const extraRatio = 1 - mainRatio;

        chartContainerRef.current!.style.flex = `${mainRatio * 10}`;
        chartExtraContainerRef.current!.style.flex = `${extraRatio * 10}`;
      });
    };

    const onPointerUp = (e: PointerEvent) => {
      if (isResizing.current) {
        isResizing.current = false;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
          rafId.current = null;
        }
      }
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', onPointerUp);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerUp);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  const handleAddSeries = (_: CurrencyTypeEnum, tenor: string, delta: string) => {
    const id = `CMVOL_${delta}_${tenor}_${currency}`;
    const type = id.includes('FLY') || id.includes('RR') ? 'fly' : 'vol';
    const label = makeSeriesLabel(id);
    addSeries({
      rawId: id,
      type,
      label,
      seriesVolsCallback: (queries) =>
        fetchVolsHistorical({
          endDate: getCurrentDate(),
          queries,
          options: { isNewVol: true },
        }),
    });
  };

  const seriesExists = useCallback(
    (_: CurrencyTypeEnum, tenor: string, delta: string) => {
      const id = `CMVOL_${delta}_${tenor}_${currency}`;
      return Boolean(series[id]);
    },
    [series, currency],
  );

  useCrosshairMove({
    chartRef: chart,
    setHoverData: setPerpetualHoverData,
    series: series,
  });

  return (
    <div
      className={`market-history-wrapper ${isWrapperLoading ? 'chart-wrapper-loading' : ''}`}
      ref={chartWrapperRef}
    >
      <div className="chart-controls-buttons">
        <MarketHistoryRangeControls
          initDate={new Date()}
          currentChartType={chartType}
          chartTypeCallback={(type: ChartTypeEnum) => setChartType(type)}
          handleApplyCalendar={handleApplyCalendar}
          handleSetRange={(newRange) => {
            setRange(newRange);
            handleRangeChange(newRange);
          }}
          disabled={
            isChartLoading || isFetchingCmvolSeries || isFetchingPerpetualSeries || !isConnected
          }
          range={range}
        />

        <div className="add-series-menu-button">
          <AddSeriesMenu onApply={handleAddSeries} seriesExists={seriesExists} isMarketData />
        </div>
      </div>

      <div className="chart-area-graph">
        <div className="chart-area-graph-header" style={chipStyle(isChartLoading)}>
          <MarketHistoryHeader
            chartType={chartType}
            isLoading={isChartLoading}
            hoverData={perpetualHoverData}
            currency={currency}
          />
          <Button onClick={() => handleRangeChange(range)} className="reset-vols-btn">
            <span>Refresh</span>
          </Button>
        </div>

        <div className="vol-timeseries-chip" style={chipStyle(isChartLoading)}>
          {Object.values(series)
            .filter((s) => s.type === 'vol' || s.type === 'future')
            .map((s) => (
              <VolSeriesChip key={s.id} series={s} removable callBack={() => removeSeries(s.id)} />
            ))}
        </div>

        <div
          className={`chart-box-container ${!isChartLoading && 'chart-transition'}`}
          ref={chartBoxContainerRef}
          style={chipStyle(isChartLoading)}
        >
          <Box
            ref={chartContainerRef}
            className={`chart-box ${hasFlyChart ? 'has-chart-extra' : ''}`}
          >
            <div className="watermark"></div>
          </Box>
          {hasFlyChart && (
            <div
              className="resize-bar"
              ref={resizeBarRef}
              onPointerDown={(e) => {
                isResizing.current = true;
                resizeBarRef.current!.setPointerCapture(e.pointerId);
              }}
            ></div>
          )}
          <div className="vol-timeseries-chip">
            {Object.values(series)
              .filter((s) => s.type === 'fly')
              .map((s) => (
                <VolSeriesChip
                  key={s.id}
                  series={s}
                  removable
                  callBack={() => removeSeries(s.id)}
                />
              ))}
          </div>
          <Box
            ref={chartExtraContainerRef}
            className="charts-extra"
            style={{ display: hasFlyChart ? 'block' : 'none' }}
          />
        </div>

        {isChartLoading && (
          <div className="market-chart-loading">
            <CircularProgress size={40} />
          </div>
        )}
      </div>
    </div>
  );
};
